import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/app-shell.tsx';
import { SectionMapping } from '../components/section-mapping.tsx';
import { Button, ErrorMessage, Field, Input } from '../components/ui.tsx';
import {
  ApiError,
  type HealthCheckResult,
  mappingApi,
  orgApi,
  type Site,
  type SiteStatus,
  siteSecretApi,
  sitesApi,
} from '../lib/api.ts';

/**
 * Pantalla de Sitios (§10.1).
 *
 * El objetivo de esta pantalla es que alguien que entra después de un fallo
 * sepa **qué hacer**, no solo que algo va mal. Por eso cada estado lleva su
 * explicación y su acción, y los errores del conector llegan con la pista que
 * generó la taxonomía del §6.5 en lugar de un código HTTP.
 */

const STATUS_STYLES: Record<SiteStatus, { dot: string; label: string; tone: string }> = {
  active: { dot: '#00a32a', label: 'Conectado', tone: 'text-[var(--color-muted)]' },
  pending: { dot: '#dba617', label: 'Pendiente', tone: 'text-[var(--color-muted)]' },
  degraded: { dot: '#dba617', label: 'Con problemas', tone: 'text-amber-700' },
  needs_reauth: { dot: '#d63638', label: 'Requiere reconectar', tone: 'text-[var(--color-danger)]' },
  needs_plugin_update: {
    dot: '#d63638',
    label: 'Plugin desactualizado',
    tone: 'text-[var(--color-danger)]',
  },
  disabled: { dot: '#8c8f94', label: 'Desactivado', tone: 'text-[var(--color-muted)]' },
};

export function SitesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showConnect, setShowConnect] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['sites'],
    queryFn: sitesApi.list,
  });

  const sites = data?.sites ?? [];

  // Cuántos periódicos caben en el plan. Es informativo: quien decide si se
  // puede conectar otro es el servidor, que lo comprueba al intentarlo.
  const { data: limits } = useQuery({ queryKey: ['org', 'limits'], queryFn: orgApi.limits });
  const atLimit = limits ? limits.usage.sites >= limits.limits.maxSites : false;

  return (
    <AppShell title={t('sites.title')}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="max-w-prose text-sm text-[var(--color-muted)]">{t('sites.subtitle')}</p>
          {limits ? (
            <p
              className={`mt-2 text-sm font-medium ${atLimit ? 'text-[var(--color-warning)]' : 'text-[var(--color-ink)]'}`}
            >
              {t('sites.usage', { used: limits.usage.sites, max: limits.limits.maxSites })}
              {atLimit ? <span className="font-normal"> — {t('sites.limitReached')}</span> : null}
            </p>
          ) : null}
        </div>
        <div className="w-auto shrink-0">
          <Button type="button" disabled={atLimit} onClick={() => setShowConnect(true)}>
            {t('sites.connect')}
          </Button>
        </div>
      </div>

      {showConnect ? (
        <ConnectWizard
          onClose={() => setShowConnect(false)}
          onConnected={() => {
            void queryClient.invalidateQueries({ queryKey: ['sites'] });
            void queryClient.invalidateQueries({ queryKey: ['org', 'limits'] });
          }}
        />
      ) : null}

      {error instanceof ApiError ? <ErrorMessage>{error.message}</ErrorMessage> : null}

      {isLoading ? (
        <p className="text-sm text-[var(--color-muted)]">{t('common.loading')}</p>
      ) : sites.length === 0 ? (
        <EmptyState onConnect={() => setShowConnect(true)} />
      ) : (
        <ul className="space-y-3">
          {sites.map((site) => (
            <SiteCard key={site.id} site={site} />
          ))}
        </ul>
      )}
    </AppShell>
  );
}

function EmptyState({ onConnect }: { onConnect: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-dashed border-[var(--color-line)] p-10 text-center">
      <p className="mb-2 font-medium">{t('sites.empty.title')}</p>
      <p className="mx-auto mb-5 max-w-md text-sm text-[var(--color-muted)]">{t('sites.empty.body')}</p>
      <div className="mx-auto w-48">
        <Button type="button" onClick={onConnect}>
          {t('sites.connect')}
        </Button>
      </div>
    </div>
  );
}

function SiteCard({ site }: { site: Site }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<HealthCheckResult | null>(null);
  const [showMapping, setShowMapping] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const style = STATUS_STYLES[site.status];

  // Se consulta siempre, no solo al abrir el mapeo: el número de secciones sin
  // asignar es un aviso que hay que ver desde la tarjeta, porque publicar con
  // una de ellas en este sitio fallaría.
  const { data: mapping } = useQuery({
    queryKey: ['mapping', site.id],
    queryFn: () => mappingApi.get(site.id),
    enabled: site.status !== 'pending',
  });

  const rotate = useMutation({
    mutationFn: () => siteSecretApi.rotate(site.id),
    onSuccess: (data) => {
      setNotice(data.rotated ? t('sites.rotateOk') : (data.error?.message ?? null));
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });

  const check = useMutation({
    mutationFn: () => sitesApi.healthCheck(site.id),
    onSuccess: (data) => {
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      // Comprobar refresca las categorías del sitio en el servidor, así que el
      // mapeo que hubiera en pantalla ya no es el bueno. Sin esta línea, quien
      // pulsa "Comprobar" y abre las categorías sigue viendo las de antes —o
      // ninguna, si es la primera vez— y parece que no funcionó.
      void queryClient.invalidateQueries({ queryKey: ['mapping', site.id] });
    },
  });

  const disconnect = useMutation({
    mutationFn: () => sitesApi.disconnect(site.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      void queryClient.invalidateQueries({ queryKey: ['org', 'limits'] });
    },
  });

  return (
    <li className="rounded-xl border border-[var(--color-line)] bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span aria-hidden="true" style={{ color: style.dot }}>
              ●
            </span>
            <h2 className="truncate font-medium">{site.name}</h2>
            <span className={`text-xs ${style.tone}`}>{style.label}</span>
          </div>

          <a
            href={site.baseUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-1 block truncate text-sm text-[var(--color-muted)] underline"
          >
            {site.baseUrl}
          </a>

          <p className="mt-2 text-xs text-[var(--color-muted)]">
            {site.wpVersion ? `WordPress ${site.wpVersion}` : null}
            {site.phpVersion ? ` · PHP ${site.phpVersion}` : null}
            {site.pluginVersion ? ` · conector ${site.pluginVersion}` : null}
            {/* Se muestra solo cuando NO es el transporte normal: es una
                particularidad del sitio que conviene que se vea. */}
            {site.transport === 'rest_route' ? ` · ${t('sites.altTransport')}` : null}
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => check.mutate()}
            disabled={check.isPending}
            className="rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-sm hover:bg-[var(--color-surface)] disabled:opacity-50"
          >
            {check.isPending ? t('sites.checking') : t('sites.check')}
          </button>

          <button
            type="button"
            onClick={() => setShowMapping((open) => !open)}
            className="rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-sm hover:bg-[var(--color-surface)]"
          >
            {t('mapping.open')}
          </button>

          <button
            type="button"
            onClick={() => {
              if (window.confirm(t('sites.rotateConfirm', { name: site.name }))) {
                rotate.mutate();
              }
            }}
            disabled={rotate.isPending}
            className="rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-sm hover:bg-[var(--color-surface)] disabled:opacity-50"
          >
            {rotate.isPending ? t('sites.rotating') : t('sites.rotate')}
          </button>

          <button
            type="button"
            onClick={() => {
              if (window.confirm(t('sites.disconnectConfirm', { name: site.name }))) {
                disconnect.mutate();
              }
            }}
            disabled={disconnect.isPending}
            className="rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-sm text-[var(--color-danger)] hover:bg-red-50 disabled:opacity-50"
          >
            {t('sites.disconnect')}
          </button>
        </div>
      </div>

      {/* El último error se muestra aunque no se haya pulsado comprobar: quien
          entra a esta pantalla suele venir porque algo falló. */}
      {site.lastErrorMessage && site.status !== 'active' ? (
        <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-[var(--color-danger)]">
          {site.lastErrorMessage}
        </div>
      ) : null}

      {notice ? (
        <p className="mt-4 rounded-lg bg-[var(--color-surface)] px-3 py-2 text-sm">{notice}</p>
      ) : null}

      {/* Aviso permanente, no solo dentro del mapeo: quien mira la lista tiene
          que saber que este sitio no puede recibir ciertas secciones. */}
      {mapping && mapping.missingSectionIds.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowMapping(true)}
          className="mt-4 block w-full rounded-lg bg-amber-50 px-3 py-2 text-left text-sm text-amber-800 hover:bg-amber-100"
        >
          ⚠ {t('mapping.missingWarning', { count: mapping.missingSectionIds.length })}
        </button>
      ) : null}

      {result ? <HealthResult result={result} /> : null}

      {showMapping ? (
        <div className="mt-4 border-t border-[var(--color-line)] pt-4">
          <h3 className="mb-3 text-sm font-medium">{t('mapping.title')}</h3>
          <SectionMapping siteId={site.id} />
        </div>
      ) : null}
    </li>
  );
}

function HealthResult({ result }: { result: HealthCheckResult }) {
  const { t } = useTranslation();

  if (!result.ok) {
    return (
      <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm">
        <p className="font-medium text-[var(--color-danger)]">{result.error?.message}</p>
        {result.error?.hint ? <p className="mt-1 text-[var(--color-muted)]">{result.error.hint}</p> : null}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg bg-[var(--color-surface)] px-4 py-3 text-sm">
      <p className="font-medium text-green-700">{t('sites.checkOk', { ms: result.latency_ms })}</p>

      {result.health ? (
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-[var(--color-muted)] sm:grid-cols-3">
          <div>
            <dt className="inline">{t('sites.info.timezone')}: </dt>
            <dd className="inline">{result.health.wp.timezone || 'UTC'}</dd>
          </div>
          <div>
            <dt className="inline">{t('sites.info.seo')}: </dt>
            <dd className="inline">{result.health.seo_plugin}</dd>
          </div>
          <div>
            <dt className="inline">{t('sites.info.author')}: </dt>
            <dd className="inline">{result.health.author.login ?? '—'}</dd>
          </div>
        </dl>
      ) : null}

      {/* Avisos: el sitio funciona, pero hay cosas que darán problemas luego. */}
      {result.warnings && result.warnings.length > 0 ? (
        <ul className="mt-3 space-y-1">
          {result.warnings.map((warning) => (
            <li key={warning} className="text-xs text-amber-700">
              ⚠ {warning}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Asistente de conexión.
 *
 * Dos pasos y no uno, porque el segundo ocurre **fuera del panel**: hay que ir
 * al wp-admin del periódico y autorizar allí. Enseñar eso explícitamente evita
 * que alguien se quede esperando a que "termine" algo que depende de él.
 */
function ConnectWizard({ onClose, onConnected }: { onClose: () => void; onConnected: () => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');

  const start = useMutation({
    mutationFn: () => sitesApi.startPairing(url.trim(), name.trim() || undefined),
  });

  return (
    <div className="mb-6 rounded-xl border border-[var(--color-line)] bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-medium">{t('sites.wizard.title')}</h2>
        <button type="button" onClick={onClose} className="text-sm text-[var(--color-muted)] underline">
          {t('common.cancel')}
        </button>
      </div>

      {start.data ? (
        <div>
          <div className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm">
            <p className="font-medium text-green-800">
              {t('sites.wizard.detected', { version: start.data.detected.pluginVersion })}
            </p>
          </div>

          <ol className="mb-5 ml-5 list-decimal space-y-2 text-sm">
            <li>{t('sites.wizard.step1')}</li>
            <li>{t('sites.wizard.step2')}</li>
            <li>{t('sites.wizard.step3')}</li>
          </ol>

          <a
            href={start.data.connect_url}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-block rounded-lg bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            {t('sites.wizard.openAdmin')}
          </a>

          <p className="mt-3 text-xs text-[var(--color-muted)]">
            {t('sites.wizard.expires', {
              time: new Date(start.data.expires_at).toLocaleTimeString('es-DO', {
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}
          </p>

          <div className="mt-5 border-t border-[var(--color-line)] pt-4">
            <button
              type="button"
              onClick={() => {
                onConnected();
                onClose();
              }}
              className="text-sm text-[var(--color-brand)] underline"
            >
              {t('sites.wizard.done')}
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            start.mutate();
          }}
        >
          {start.error instanceof ApiError ? (
            <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm">
              <p className="text-[var(--color-danger)]">{start.error.message}</p>
              {start.error.hint ? (
                <p className="mt-1 text-xs text-[var(--color-muted)]">{start.error.hint}</p>
              ) : null}
            </div>
          ) : null}

          <Field id="site-url" label={t('sites.wizard.urlLabel')} hint={t('sites.wizard.urlHint')}>
            <Input
              id="site-url"
              required
              placeholder="https://miperiodico.com"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          </Field>

          <Field id="site-name" label={t('sites.wizard.nameLabel')}>
            <Input
              id="site-name"
              placeholder={t('sites.wizard.namePlaceholder')}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Button type="submit" loading={start.isPending} disabled={url.trim().length === 0}>
            {start.isPending ? t('sites.wizard.checking') : t('sites.wizard.submit')}
          </Button>
        </form>
      )}
    </div>
  );
}
